// src/pages/Cart.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useCart } from "../context/CartContext";
import { useNavigate, Link } from "react-router-dom";
import QtyControlPro from "../components/QtyControlPro";
import "../styles/qty-control.css";

import { db } from "../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";

/* ---------- Helpers variantes (affiche Couleur / Taille / Label) ---------- */
const prettyVariant = (it = {}) => {
  // 1) label explicite
  if (it.variantLabel && String(it.variantLabel).trim()) return it.variantLabel;

  // 2) objet variant complet (si tu le stockes)
  const v = it.variant || {};
  const pick = (obj, keys) => {
    for (const k of keys) {
      const val = obj?.[k];
      if (val !== undefined && val !== null && String(val).trim()) return String(val).trim();
    }
    return "";
  };
  const color = pick(v, ["color", "couleur", "Colour", "Color", "option1"]);
  const size = pick(v, ["size", "taille", "Size", "option2"]);
  const name = pick(v, ["name", "label", "variant", "title"]);
  const parts = [color, size, name].filter(Boolean);
  if (parts.length) return parts.join(" / ");

  // 3) variantKey (chez toi: taille "40/41/42" ou "default")
  if (it.variantKey && it.variantKey !== "default" && it.variantKey !== "|") {
    const s = String(it.variantKey).replace(/\|/g, " / ").trim();
    // Si c'est un nombre => on l'affiche comme une taille
    if (/^\d{1,3}$/.test(s)) return `Taille ${s}`;
    return s;
  }

  return "—";
};

/* ---------- Lien produit (slug prioritaire) ---------- */
const getProductHref = (it = {}) => {
  const slug = it.slug || it.productSlug || it.handle || "";
  if (slug && typeof slug === "string") return `/p/${slug}`;
  if (it.productId) return `/p/${it.productId}`;
  return "#";
};

/* ---------- Nombre robuste (tolère "2") ---------- */
const toInt = (v) => {
  if (v == null) return NaN;
  const n = Number(String(v).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? Math.round(n) : NaN;
};

const isManagedStock = (n) => typeof n === "number" && n >= 0;

/** ===== Base API ===== */
function getApiBase() {
  const env = (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "");
  if (env) return env;
  if (typeof window !== "undefined" && window.location.hostname === "localhost") {
    return "http://localhost:5000";
  }
  return ""; // même domaine (Vercel)
}
const API_BASE = getApiBase();

// Helper POST JSON
async function postJSON(url, body) {
  const resp = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const text = await resp.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    console.error("Réponse non-JSON (souvent HTML):", text.slice(0, 400));
    throw new Error(`Réponse non-JSON (${resp.status}) depuis ${url}`);
  }
  if (!resp.ok) {
    const msg = data?.provider_message || data?.message || data?.error || `HTTP ${resp.status}`;
    throw new Error(msg);
  }
  return data;
}

/* ======================= Helpers PayGate (TG) ======================= */
function normalizeTogoPhone(raw = "") {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("228")) return digits.slice(3);
  if (digits.length === 8) return digits;
  return null;
}

/* ======================= Frais ======================= */
const FEE_RULES = {
  MOBILE_MONEY: {
    TMONEY: { percent: 3, fixed: 0 },
    FLOOZ: { percent: 2.5, fixed: 0 },
    default: { percent: 3, fixed: 0 },
  },
  COD: { default: { percent: 0, fixed: 0 } },
  PICKUP: { default: { percent: 0, fixed: 0 } },
  DEFAULT: { default: { percent: 0, fixed: 0 } },
};

function computeFeeXOF(baseAmountXOF, method, network) {
  const m = FEE_RULES[method] || FEE_RULES.DEFAULT;
  const rule = (m && (m[network] || m.default)) || FEE_RULES.DEFAULT.default;
  const pctPart = Math.round((baseAmountXOF * (rule.percent || 0)) / 100);
  const fixedPart = Math.round(rule.fixed || 0);
  return Math.max(0, pctPart + fixedPart);
}

function isValidXof(n) {
  return Number.isFinite(n) && Math.round(n) === n && n >= 100;
}

/* ======================= Stock helpers (Firestore) ======================= */
/**
 * Retourne le stock dispo d'une ligne (selon variantKey = taille).
 * - si variantKey != "default": on cherche variants[].size == variantKey
 * - sinon: on prend product.stock
 * - si stock non géré (-1 ou absent): retourne null
 */
function getAvailableForLine(productDoc, variantKey) {
  const key = String(variantKey || "default");
  const p = productDoc || {};

  if (key !== "default") {
    const vars = Array.isArray(p.variants) ? p.variants : [];
    const found = vars.find((v) => String(v?.size) === key);
    const s = found?.stock;
    return isManagedStock(s) ? s : null;
  }

  const s = p.stock;
  return isManagedStock(s) ? s : null;
}

export default function Cart() {
  const { items, updateQty, removeItem, clear, total } = useCart();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [err, setErr] = useState("");

  const [identifier] = useState(() => `cmd-${Date.now()}`);

  const [customer, setCustomer] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    address: "",
  });

  const [paymentMethod, setPaymentMethod] = useState("MOBILE_MONEY"); // "MOBILE_MONEY" | "COD" | "PICKUP"
  const [payNetwork, setPayNetwork] = useState("TMONEY"); // "TMONEY" | "FLOOZ"

  const currency = "FCFA";
  const nf = new Intl.NumberFormat("fr-FR");

  // total est déjà en XOF (entier) dans ton contexte
  const amountItemsXOF = useMemo(() => Math.max(100, Math.round(total || 0)), [total]);

  const feeXOF = useMemo(() => {
    const networkKey = paymentMethod === "MOBILE_MONEY" ? payNetwork : "default";
    return computeFeeXOF(amountItemsXOF, paymentMethod, networkKey);
  }, [amountItemsXOF, paymentMethod, payNetwork]);

  const amountToChargeXOF = useMemo(() => amountItemsXOF + feeXOF, [amountItemsXOF, feeXOF]);

  /* ======================= STOCK: fetch live products ======================= */
  const [stockByLineKey, setStockByLineKey] = useState({}); // key: `${productId}__${variantKey}` => number|null
  const [stockLoading, setStockLoading] = useState(false);

  const lineKey = (productId, variantKey) => `${productId}__${String(variantKey || "default")}`;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        if (!items.length) {
          setStockByLineKey({});
          return;
        }
        setStockLoading(true);

        // charger tous les products uniques
        const uniqueIds = Array.from(new Set(items.map((it) => it.productId).filter(Boolean)));
        const productDocs = {};
        await Promise.all(
          uniqueIds.map(async (id) => {
            try {
              const snap = await getDoc(doc(db, "products", id));
              productDocs[id] = snap.exists() ? snap.data() : null;
            } catch {
              productDocs[id] = null;
            }
          })
        );

        const nextMap = {};
        for (const it of items) {
          if (!it.productId) continue;
          const pDoc = productDocs[it.productId];
          const avail = getAvailableForLine(pDoc, it.variantKey);
          nextMap[lineKey(it.productId, it.variantKey)] = avail; // number ou null (non géré / inconnu)
        }

        if (!cancelled) setStockByLineKey(nextMap);
      } finally {
        if (!cancelled) setStockLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((x) => `${x.productId}:${x.variantKey}`).join("|")]);

  /* ---------- Max quantité par ligne : stock live en priorité ---------- */
  const getLineMax = (it) => {
    const k = lineKey(it.productId, it.variantKey);
    const avail = stockByLineKey[k];
    if (Number.isFinite(avail) && avail > 0) return avail;
    // si stock géré et =0 => max 0 (on empêchera la commande)
    if (avail === 0) return 0;

    // fallback: champs éventuels posés à l'ajout
    const tries = [
      it?.maxQty,
      it?.variant?.maxQty,
      it?.variant?.stock,
      it?.stock,
      it?.available,
      it?.availability,
      it?.inventory,
      it?.max,
      it?.quantityAvailable,
    ];
    for (const candidate of tries) {
      const n = toInt(candidate);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return 99;
  };

  /* ---------- Clamp automatique quand stock live est connu ---------- */
  useEffect(() => {
    for (const it of items) {
      const max = getLineMax(it);
      // si stock géré et max = 0 => on force qty à 1 mais on bloquera canPay
      if (Number.isFinite(max) && max > 0 && it.qty > max) {
        updateQty(it.productId, it.variantKey, max);
      }
      if (it.qty < 1) updateQty(it.productId, it.variantKey, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockByLineKey]);

  /* ---------- Stock OK pour payer ? ---------- */
  const stockOk = useMemo(() => {
    if (!items.length) return false;
    // Si une ligne a un stock géré (=number) et qty > stock => KO
    for (const it of items) {
      const k = lineKey(it.productId, it.variantKey);
      const avail = stockByLineKey[k];
      if (Number.isFinite(avail) && isManagedStock(avail) && it.qty > avail) return false;
      if (avail === 0) return false;
    }
    return true;
  }, [items, stockByLineKey]);

  const canPay = useMemo(() => {
    const hasItems = items.length > 0;
    const nameOk = customer.name.trim().length > 1;

    if (!hasItems || !nameOk) return false;
    if (!stockOk) return false;

    if (paymentMethod === "MOBILE_MONEY") {
      return normalizeTogoPhone(customer.phone) !== null;
    }
    return customer.phone.trim().length >= 8;
  }, [items, customer, paymentMethod, stockOk]);

  /* --------- 1) MOBILE MONEY --------- */
  const createPaygateAndRedirect = async () => {
    setErr("");
    if (redirecting) return;

    if (!stockOk) {
      setErr("Stock insuffisant sur un ou plusieurs articles. Ajuste les quantités.");
      return;
    }

    const normalizedPhone = normalizeTogoPhone(customer.phone);
    if (!normalizedPhone) {
      setErr("Numéro Togo invalide (8 chiffres ou +228XXXXXXXX).");
      return;
    }
    if (!isValidXof(amountItemsXOF) || !isValidXof(amountToChargeXOF)) {
      setErr("Montant invalide. Le total doit être un entier en XOF (≥ 100).");
      return;
    }
    if (paymentMethod !== "MOBILE_MONEY") {
      setErr("Veuillez choisir « Mobile Money » pour payer par TMoney ou Flooz.");
      return;
    }

    try {
      setLoading(true);
      const body = {
        orderId: identifier,
        currency: "XOF",
        channels: "MOBILE_MONEY",
        pay_network: payNetwork,
        customer: {
          name: customer.name,
          phone: customer.phone,
          email: customer.email || undefined,
          city: customer.city || "Lome",
          address: customer.address || "Adresse",
        },
        items,
        subtotal_xof: amountItemsXOF,
      };

      const base = (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "") || API_BASE;
      const data = await postJSON(`${base}/api/paygate/create`, body);

      if (!data?.payment_url) throw new Error("payment_url manquant");
      setRedirecting(true);
      window.location.href = data.payment_url;
    } catch (e) {
      setErr(e.message || "Erreur lors de l'initialisation du paiement.");
    } finally {
      setLoading(false);
    }
  };

  /* --------- 2) COD / PICKUP --------- */
  const createOfflineOrder = async (method /* 'COD' | 'PICKUP' */) => {
    setErr("");

    if (!stockOk) {
      setErr("Stock insuffisant sur un ou plusieurs articles. Ajuste les quantités.");
      return;
    }

    try {
      setLoading(true);
      const body = {
        orderId: identifier,
        payment_method: method,
        customer,
        items,
        currency: "XOF",
        subtotal_xof: amountItemsXOF,
        fee_xof: 0,
        total_xof: amountItemsXOF,
        delivery: method === "COD" ? { mode: "delivery" } : { mode: "pickup" },
      };

      const base = (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "") || API_BASE;
      const data = await postJSON(`${base}/api/orders/create`, body);

      navigate(`/checkout/success?orderId=${encodeURIComponent(data.orderId || identifier)}&offline=1`);
    } catch (e) {
      setErr(e.message || "Impossible d’enregistrer la commande.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4">
      <h4 className="mb-3">Panier</h4>

      {items.length === 0 ? (
        <div className="text-muted">Votre panier est vide.</div>
      ) : (
        <div className="row g-4">
          {/* Items */}
          <div className="col-lg-8">
            <div className="card shadow-sm">
              <div className="card-body">
                {stockLoading && (
                  <div className="alert alert-light border small mb-3">
                    Vérification du stock en cours…
                  </div>
                )}

                {items.map((it, i) => {
                  const lineTotal = nf.format(Math.round(it.price * it.qty));
                  const unitPrice = nf.format(Math.round(it.price));
                  const href = getProductHref(it);

                  const k = lineKey(it.productId, it.variantKey);
                  const avail = stockByLineKey[k]; // number ou null
                  const managed = Number.isFinite(avail);
                  const lineMax = getLineMax(it);
                  const out = managed && avail === 0;
                  const over = managed && Number.isFinite(avail) && it.qty > avail;

                  return (
                    <div
                      key={`${it.productId}-${it.variantKey}-${i}`}
                      className="cart-line d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2 gap-sm-3 border-bottom py-3"
                    >
                      {it.image && (
                        <Link to={href} className="d-inline-block">
                          <img
                            src={it.image}
                            alt={it.title}
                            className="cart-thumb"
                            style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8 }}
                          />
                        </Link>
                      )}

                      <div className="flex-grow-1 w-100">
                        <Link to={href} className="fw-semibold text-decoration-none text-dark">
                          <span className="text-truncate d-inline-block" style={{ maxWidth: 520 }}>
                            {it.title}
                          </span>
                        </Link>

                        <div className="text-muted small">
                          {(() => {
                            const label = prettyVariant(it);
                            return label !== "—"
                              ? <>Variante : <span className="text-body-secondary">{label}</span></>
                              : "—";
                          })()}
                        </div>

                        <div className="text-muted small mt-1">
                          Prix unitaire : <strong>{unitPrice} {currency}</strong>
                        </div>

                        <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
                          {managed ? (
                            <span className={`badge ${out ? "text-bg-danger" : "text-bg-secondary"}`}>
                              Stock dispo : {avail}
                            </span>
                          ) : (
                            <span className="badge text-bg-light border text-dark">
                              Stock non géré
                            </span>
                          )}

                          {over && (
                            <span className="badge text-bg-warning">
                              Quantité trop élevée
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions & prix côté droit */}
                      <div className="w-100 w-sm-auto ms-0 ms-sm-auto text-sm-end">
                        <QtyControlPro
                          value={it.qty}
                          min={1}
                          max={lineMax === 0 ? 1 : lineMax}
                          size="md"
                          onChange={(q) => {
                            const n = toInt(q) || 1;

                            // si stock géré et dispo=0 => on refuse d'augmenter
                            if (managed && avail === 0) return;

                            const max = lineMax > 0 ? lineMax : 99;
                            const clamped = Math.max(1, Math.min(max, n));
                            if (clamped !== it.qty) updateQty(it.productId, it.variantKey, clamped);
                          }}
                          title={managed ? `Quantité (stock ${avail})` : `Quantité`}
                        />

                        <div className="fw-bold mt-2">{lineTotal} {currency}</div>

                        <div className="mt-2 d-flex justify-content-end gap-3">
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger p-0"
                            onClick={() => removeItem(it.productId, it.variantKey)}
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Résumé & paiement */}
          <div className="col-lg-4">
            <div className="card shadow-sm">
              <div className="card-body">
                <h6 className="mb-3">Client</h6>

                <div className="mb-2">
                  <label className="form-label">Nom</label>
                  <input
                    className="form-control"
                    value={customer.name}
                    onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
                    required
                  />
                </div>

                <div className="mb-2">
                  <label className="form-label">Téléphone</label>
                  <input
                    className="form-control"
                    value={customer.phone}
                    onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))}
                    required
                  />
                </div>

                <div className="mb-2">
                  <label className="form-label">Ville</label>
                  <input
                    className="form-control"
                    value={customer.city}
                    onChange={(e) => setCustomer((c) => ({ ...c, city: e.target.value }))}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Email (recommandé pour facture)</label>
                  <input
                    className="form-control"
                    type="email"
                    value={customer.email}
                    onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Moyen de paiement</label>
                  <div className="d-flex flex-column gap-2">
                    <label className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="pay"
                        checked={paymentMethod === "MOBILE_MONEY"}
                        onChange={() => setPaymentMethod("MOBILE_MONEY")}
                      />
                      <span className="form-check-label">Mobile Money (TMoney / Flooz)</span>
                    </label>

                    <label className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="pay"
                        checked={paymentMethod === "COD"}
                        onChange={() => setPaymentMethod("COD")}
                      />
                      <span className="form-check-label">Payer à la livraison</span>
                    </label>

                    <label className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="pay"
                        checked={paymentMethod === "PICKUP"}
                        onChange={() => setPaymentMethod("PICKUP")}
                      />
                      <span className="form-check-label">Payer lors du retrait (pick-up)</span>
                    </label>
                  </div>

                  {paymentMethod === "MOBILE_MONEY" && (
                    <div className="mt-2">
                      <label className="form-label">Réseau Mobile Money</label>
                      <select
                        className="form-select"
                        value={payNetwork}
                        onChange={(e) => setPayNetwork(e.target.value)}
                      >
                        <option value="TMONEY">TMoney (Togocom)</option>
                        <option value="FLOOZ">Flooz (Moov)</option>
                      </select>
                      <div className="form-text">Saisir un numéro TG : 8 chiffres ou +228XXXXXXXX.</div>
                    </div>
                  )}
                </div>

                <h6>Résumé</h6>
                <div className="d-flex justify-content-between">
                  <span>Sous-total articles</span>
                  <strong>{nf.format(amountItemsXOF)} {currency}</strong>
                </div>

                <div className="d-flex justify-content-between">
                  {paymentMethod === "MOBILE_MONEY" ? (
                    <>
                      <span>Frais de paiement</span>
                      <strong>{nf.format(feeXOF)} {currency}</strong>
                    </>
                  ) : paymentMethod === "COD" ? (
                    <span className="text-muted small">
                      Les frais de livraison vous seront communiqués par un de nos commerciaux.
                    </span>
                  ) : null}
                </div>

                <div className="d-flex justify-content-between border-top pt-2">
                  <span>Total à payer</span>
                  <strong>{nf.format(amountToChargeXOF)} {currency}</strong>
                </div>

                {!stockOk && (
                  <div className="alert alert-warning mt-3">
                    Stock insuffisant sur un ou plusieurs articles. Ajuste les quantités.
                  </div>
                )}

                {err && <div className="alert alert-danger">{err}</div>}

                <div className="d-grid gap-2">
                  {paymentMethod === "MOBILE_MONEY" ? (
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={createPaygateAndRedirect}
                      disabled={!canPay || loading || redirecting}
                    >
                      {redirecting ? "Redirection…" : loading ? "Paiement (PayGate)…" : "Payer (TMoney / Flooz)"}
                    </button>
                  ) : paymentMethod === "COD" ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => createOfflineOrder("COD")}
                      disabled={!canPay || loading}
                    >
                      Confirmer la commande (Paiement à la livraison)
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => createOfflineOrder("PICKUP")}
                      disabled={!canPay || loading}
                    >
                      Confirmer la commande (Paiement au pick-up)
                    </button>
                  )}

                  <button type="button" className="btn btn-outline-secondary" onClick={clear} disabled={loading}>
                    Vider le panier
                  </button>
                </div>
              </div>
            </div>

            <div className="alert alert-info mt-3">
              {paymentMethod === "MOBILE_MONEY"
                ? "Vous serez redirigé vers PayGate pour terminer le paiement."
                : paymentMethod === "COD"
                ? "Votre commande sera payée à la livraison. Les frais de livraison vous seront communiqués."
                : "Votre commande sera payée lors du retrait en boutique."}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}